/**
 * Backoffice · Cuestionario de feedback: campañas + resultados.
 * Endpoints /api/backoffice/feedback y /api/backoffice/feedback/campaigns.
 */

import { ApiClient, buildQuery } from './client';

export interface FeedbackCampaign {
  id: string;
  name: string;
  startDate: number;
  endDate: number;
  active: boolean;
  /** Nº de respuestas de la campaña (presente en resultados y en el listado de gestión). */
  responseCount?: number;
}

export interface FeedbackResponseItem {
  id: string;
  name: string | null;
  nps: number;
  npsReason: string | null;
  progress: number | null;
  levelFit: string | null;
  easeOfUse: number | null;
  topFeatures: string[];
  improvement: string | null;
  createdAt: number;
}

export interface BackofficeFeedback {
  campaigns: FeedbackCampaign[];
  selectedCampaignId: string | null;
  total: number;
  nps: {
    score: number;
    promoters: number;
    passives: number;
    detractors: number;
    promoterPct: number;
    passivePct: number;
    detractorPct: number;
  };
  averages: {
    progress: number | null;
    easeOfUse: number | null;
  };
  levelFit: Record<'easy' | 'right' | 'hard', number>;
  topFeature: Record<'stories' | 'conversation' | 'vocabulary' | 'gamification' | 'rewards', number>;
  responses: FeedbackResponseItem[];
}

export interface CampaignInput {
  name: string;
  startDate: number;
  endDate: number;
}

/** Resultados (NPS + respuestas) de la campaña seleccionada (o la activa/reciente). */
export function getBackofficeFeedback(campaignId?: string): Promise<BackofficeFeedback> {
  const qs = campaignId ? buildQuery({ campaignId }) : '';
  return ApiClient.get<BackofficeFeedback>(`/backoffice/feedback${qs}`);
}

/** Listado de campañas (con flag activa y nº de respuestas) para gestión. */
export function listFeedbackCampaigns(): Promise<FeedbackCampaign[]> {
  return ApiClient.get<FeedbackCampaign[]>('/backoffice/feedback/campaigns');
}

export function createFeedbackCampaign(input: CampaignInput): Promise<FeedbackCampaign> {
  return ApiClient.post<FeedbackCampaign>('/backoffice/feedback/campaigns', input);
}

export function updateFeedbackCampaign(
  id: string,
  input: Partial<CampaignInput>
): Promise<FeedbackCampaign> {
  return ApiClient.patch<FeedbackCampaign>(`/backoffice/feedback/campaigns/${id}`, input);
}

export function deleteFeedbackCampaign(id: string): Promise<{ ok: boolean }> {
  return ApiClient.delete<{ ok: boolean }>(`/backoffice/feedback/campaigns/${id}`);
}
